import React, { lazy, Suspense, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LoadingOutlined } from '@ant-design/icons'

// Lazy so opening the modal doesn't put the whole auth flow in the main bundle;
// the local Suspense keeps the root one from blanking the page underneath.
const AuthFlow = lazy(() => import('./AuthFlow'))

/**
 * Overlay at every width: the auth card floating over the page the user was on
 * (blurred + dimmed backdrop covering the navbar too, card centred in the
 * viewport — kit .ovl / .mdl-wrap; 16px inset under 600px, the card bounded to
 * the screen and scrolling inside — never a bottom sheet, William 30 Sept 2026).
 * Rendered by App when an auth path carries `state.backgroundLocation`; closing
 * navigates back to it.
 */
const AuthModal = () => {
    const { t } = useTranslation()

    const navigate = useNavigate()
    const location = useLocation()

    const background = location.state?.backgroundLocation

    const close = () => {
        if (background) {
            navigate(background.pathname + (background.search || '') + (background.hash || ''))
        } else {
            navigate('/')
        }
    }

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close()
        }
        document.addEventListener('keydown', onKeyDown)

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = previousOverflow
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="fixed inset-0 z-[101]">
            <Helmet>
                <title>{t('SCORING.FIT - Login')}</title>
            </Helmet>

            <div
                className="absolute inset-0 bg-[rgba(14,16,23,.28)] backdrop-blur-[6px] backdrop-saturate-[1.1]"
                aria-hidden="true"
            />

            <div
                className="absolute inset-0 overflow-y-auto"
                onMouseDown={(event) => {
                    if (event.target === event.currentTarget) close()
                }}
            >
                <div
                    className="flex h-full justify-center items-center p-4 min-[601px]:p-6"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) close()
                    }}
                >
                    <div role="dialog" aria-modal="true" className="flex max-h-full w-fit max-w-full">
                        <Suspense
                            fallback={
                                <div className="flex w-[426px] max-w-full items-center justify-center py-24 text-3xl text-[#272727]">
                                    <LoadingOutlined />
                                </div>
                            }
                        >
                            <AuthFlow />
                        </Suspense>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AuthModal
